import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import SessionDetailsCard from '@cont/OneToOneLiveMentorship/SessionDetails/SessionDetailsCard';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import H5 from '@base/H5/H5';
import Image from '@base/Image';
import {
  getCardOneDetails,
  getCardTwoDetails,
  getCardThreeDetails
} from '@cont/OneToOneLiveMentorship/SessionDetails/getCardDetails';
import Divider from '@base/Divider/Divider';
import { LMPSessionStatus } from '@constants/index';
import { useEffect } from 'react';
import pushRoute from '@utils/pushRoute';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import {
  getTimeDifference,
  getTime,
  getYYYYMMDDFormat,
  addTime
} from '@utils/dates';
import { segment } from '@actions/analytics';
import {
  LMP_BOOKING_CLICKED,
  LMP_SESSION_CANCELLED_REQUESTED,
  LMP_RESCHEDULE_CLICKED,
  LMP_JOIN_SESSION_CLICKED
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useSessionStore from '@stores/live-mentorship/sessionStore';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useSessionDetails from '@api/hooks/oneToOneLiveMentorship/useSessionDetails';
import useSessionMetaDataInfo from '@api/hooks/oneToOneLiveMentorship/useSessionMetaDataInfo';
import useCancelReasons from '@api/hooks/oneToOneLiveMentorship/useCancelReasons';
import useLMPOnboardingPreferencesInfo from '@api/hooks/oneToOneLiveMentorship/useLMPOnboardingPreferencesInfo';
import { GENDER_MAPPING, LANGUAGE_MAPPING } from '../utils';

const SessionDetailDrawerDetails = ({
  sessionDetails,
  isDarkModeOn,
  goalUID
}) => {
  const {
    vertical,
    status,
    educator,
    slot,
    recordingStatus,
    uid: sessionUID,
    isWelcomeSession
  } = sessionDetails;
  const cardOneDetails = getCardOneDetails(
    slot,
    status,
    isDarkModeOn,
    recordingStatus
  );
  const cardTwoDetails = getCardTwoDetails(educator, status, isWelcomeSession);
  const cardThreeDetails = getCardThreeDetails(vertical, isDarkModeOn);

  if (!sessionDetails.uid) return null;
  return (
    <>
      <SessionDetailsCard
        cardDetails={cardOneDetails}
        goalUID={goalUID}
        sessionState={status}
        sessionUID={sessionUID}
      />
      <SessionDetailsCard cardDetails={cardTwoDetails} />
      {!isWelcomeSession && (
        <SessionDetailsCard cardDetails={cardThreeDetails} />
      )}
    </>
  );
};

const SessionDetailsForMentorHead = styled(H5)`
  margin: ${Spacings.SPACING_6B} 0px;
`;

const SessionDetailsForMentorDesc = styled.p`
  font-size: ${Spacings.SPACING_3B};
  color: var(--color-text-secondary);
  margin: ${Spacings.SPACING_4B} 0px;
  line-height: ${Spacings.SPACING_4B};
`;

const StyledImageWrapper = styled.div`
  display: inline-block;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  margin-right: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
`;

const SessionSmallHeading = styled(H6)`
  font-weight: ${Fonts.BOLD};
  color: var(--color-i-lmp);
  grid-column: span 10;
`;

const SessionHeading = styled(H2)`
  color: var(--color-text-primary);
  margin: ${Spacings.SPACING_2B} 0px;
  grid-column: span 10;
`;

const SessionDrawerHeading = ({ isK12, sessionHeader, isWelcomeSession }) => (
  <>
    <SessionSmallHeading>
      {isK12 ? '1:1 LIVE HOMEWORK HELP' : '1:1 LIVE MENTORSHIP'}
    </SessionSmallHeading>
    <SessionHeading>
      {isWelcomeSession ? 'Welcome Session' : sessionHeader}
    </SessionHeading>
  </>
);

export const SessionDetailsForMentor = ({
  sessionDetails,
  isWelcomeSession
}) => {
  return (
    <>
      {sessionDetails.uid ? (
        <>
          <SessionDetailsForMentorHead>
            {isWelcomeSession
              ? 'Your mentor will discuss the following details '
              : 'Note for the mentor'}
          </SessionDetailsForMentorHead>
          <SessionDetailsForMentorDesc>
            {sessionDetails?.comments}
          </SessionDetailsForMentorDesc>
          {sessionDetails?.attachments ? (
            <SessionDetailsForMentorHead>
              Attachments
            </SessionDetailsForMentorHead>
          ) : null}
          {sessionDetails?.attachments?.map((imageURL) => {
            return (
              <StyledImageWrapper key={imageURL}>
                <Image
                  src={imageURL.url}
                  width={112}
                  height={112}
                  alt="doubt-attachment"
                />
              </StyledImageWrapper>
            );
          })}
        </>
      ) : null}
    </>
  );
};

const SessionDetailsDrawerDiv = styled.div`
  grid-column: span 10;
  margin-top: ${Spacings.SPACING_4B};
`;

const SessionRefundStatus = styled.p`
  font-size: ${Spacings.SPACING_3B};
  color: var(--color-text-secondary);
  margin: ${Spacings.SPACING_4B} 0px;
`;

const SessionDetailsDrawer = ({
  setFooterData,
  setSessionCancellation,
  goalUID,
  setRescheduleWelcomeSession,
  setRescheduleFlowStep
}) => {
  const {
    sessionDrawerUID: sessionUID,
    setSessionDrawerUID,
    setBookLMPSessionData
  } = useSessionStore();
  const { goalInfo = {} } = useGoalInfo(goalUID);
  const { isK12 } = goalInfo;
  const { sessionDetails: sessionDetailsStore } = useSessionDetails(sessionUID);
  const {
    status,
    slot = {},
    isRebookSession,
    vertical = {},
    isWelcomeSession
  } = sessionDetailsStore;
  const { startTime } = slot;
  const { sessionMetaData } = useSessionMetaDataInfo(goalUID);
  const { sessionsLeft } = sessionMetaData;

  const sessionEndTime = addTime(new Date(startTime), slot?.duration, 'm');
  const timeDiff = getTimeDifference(null, startTime, true, false, true);
  const isLive = timeDiff < 2 && status <= 20 && new Date() < sessionEndTime;
  const isOneHourLeft = isWelcomeSession && timeDiff < 60;
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const sessionCreditsRefunded =
    sessionDetailsStore?.slot?.durationInfo.sessionDebitCount;

  const { lmpOnboardingPreferencesInfo: onBoardingPreferences } =
    useLMPOnboardingPreferencesInfo(goalUID);

  const routeGoalSegmentData = useGoalEventProps({ goalUID });
  const sessionHeader = vertical.parent?.parent?.name;
  const { mutate: fetchCancelSessionDetails } = useCancelReasons(sessionUID);

  const setRescheduleWelcomeSessionData = () => {
    setFooterData({
      button: {
        label: 'Reschedule',
        variant: 'hollow',
        theme: '',
        onClick: () => {
          setRescheduleWelcomeSession(true);
          setRescheduleFlowStep('BOOK_TIME_SLOT');
          setBookLMPSessionData({
            level5UID: vertical?.parent?.parent?.uid,
            sessionType: vertical?.parent?.parent?.name,
            selectedlevel15UID: vertical?.uid,
            level15Name: vertical?.name,
            level10Name: vertical?.parent?.name,
            seletedlevel10UID: vertical?.parent?.uid
          });

          segment.track(LMP_RESCHEDULE_CLICKED, {
            gender:
              GENDER_MAPPING[onBoardingPreferences?.selectedGender?.genderCode],
            educator_uid: sessionDetailsStore?.educator?.uid,
            educator_username: sessionDetailsStore?.educator?.firstName,
            is_educator_assigned: !!sessionDetailsStore?.educator?.uid,
            is_past_session: status > 20,
            lmp_booking_type: isRebookSession ? 'Repeat' : 'New',
            lmp_date: startTime ? getYYYYMMDDFormat(new Date(startTime)) : 'NA',
            lmp_session_balance: sessionsLeft,
            lmp_session_id: sessionDetailsStore?.uid,
            lmp_session_scheduled_duration: slot?.durationInfo?.value * 60,
            lmp_session_type: vertical?.parent?.parent?.name,
            lmp_time_slot: startTime ? getTime(startTime) : 'NA',
            session_type: 'Welcome',
            topic_group_title: vertical?.parent?.name,
            topic_group_uid: vertical?.parent?.uid,
            topic_title: vertical?.name,
            topic_uid: vertical?.uid,
            ...routeGoalSegmentData,
            language:
              LANGUAGE_MAPPING[
                onBoardingPreferences?.selectedLanguages[0]?.code
              ]
          });
        }
      }
    });
  };

  useEffect(() => {
    if (isLive) {
      setFooterData({
        button: {
          label: 'Join Session',
          variant: 'filled',
          theme: 'green',
          onClick: () => {
            setSessionDrawerUID(null);
            segment.track(LMP_JOIN_SESSION_CLICKED, {
              gender:
                GENDER_MAPPING[
                  onBoardingPreferences?.selectedGender?.genderCode
                ],
              educator_uid: sessionDetailsStore?.educator?.uid,
              educator_username: sessionDetailsStore?.educator?.firstName,
              lmp_booking_type: isRebookSession ? 'Repeat' : 'New',
              lmp_date: startTime
                ? getYYYYMMDDFormat(new Date(startTime))
                : 'NA',
              lmp_session_balance: sessionsLeft,
              lmp_session_id: sessionDetailsStore?.uid,
              lmp_session_scheduled_duration: slot?.durationInfo?.value * 60,
              lmp_session_type: vertical?.parent?.parent?.name,
              lmp_time_slot: startTime ? getTime(startTime) : 'NA',
              session_type: isWelcomeSession ? 'Welcome' : 'LMP',
              topic_group_title: vertical?.parent?.name,
              topic_group_uid: vertical?.parent?.uid,
              topic_title: vertical?.name,
              topic_uid: vertical?.uid,
              ...routeGoalSegmentData,
              language:
                LANGUAGE_MAPPING[
                  onBoardingPreferences?.selectedLanguages[0]?.code
                ]
            });
            pushRoute(
              '/goal/[goalSlug]/[goalUID]/one-to-one-livementorship/attending-session/[sessionUID]',
              `/goal/${goalInfo.slug}/${goalInfo.uid}/one-to-one-livementorship/attending-session/${sessionUID}`
            );
          }
        }
      });
    } else if (
      status === LMPSessionStatus.UPCOMING ||
      status === LMPSessionStatus.ASSIGNMENT_DUE ||
      status === LMPSessionStatus.REASSIGNED
    ) {
      if (isWelcomeSession && !isOneHourLeft) {
        setRescheduleWelcomeSessionData();
      } else {
        setFooterData({
          button: {
            label: 'Cancel session',
            variant: 'hollow',
            theme: 'red',
            onClick: () => {
              setSessionCancellation(sessionUID);
              fetchCancelSessionDetails();
              segment.track(LMP_SESSION_CANCELLED_REQUESTED, {
                gender:
                  GENDER_MAPPING[
                    onBoardingPreferences?.selectedGender?.genderCode
                  ],
                educator_uid: sessionDetailsStore?.educator?.uid,
                educator_username: sessionDetailsStore?.educator?.firstName,
                is_educator_assigned: !!sessionDetailsStore?.educator?.uid,
                lmp_booking_type: isRebookSession ? 'Repeat' : 'New',
                lmp_date: getYYYYMMDDFormat(new Date(startTime)),
                lmp_session_balance: sessionsLeft,
                session_type: 'LMP',
                lmp_session_id: sessionDetailsStore?.uid,
                lmp_session_scheduled_duration: slot?.durationInfo.value * 60,
                lmp_cancellation_subreason: 'NA',
                lmp_cancellation_additional_details: 'NA',
                lmp_session_type: vertical?.parent?.parent?.name,
                lmp_time_slot: getTime(startTime),
                topic_group_title: vertical?.parent?.name,
                topic_group_uid: vertical?.parent?.uid,
                topic_title: vertical?.name,
                topic_uid: vertical?.uid,
                ...routeGoalSegmentData,
                language:
                  LANGUAGE_MAPPING[
                    onBoardingPreferences?.selectedLanguages?.[0]?.code
                  ]
              });
            }
          }
        });
      }
    } else {
      setFooterData({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    segment.track(LMP_BOOKING_CLICKED, {
      gender: GENDER_MAPPING[onBoardingPreferences?.selectedGender?.genderCode],
      educator_uid: sessionDetailsStore?.educator?.uid,
      educator_username: sessionDetailsStore?.educator?.firstName,
      is_educator_assigned: !!sessionDetailsStore?.educator?.uid,
      is_past_session: status > 20,
      last_primary_source_section: 'LMP',
      session_type: 'LMP',
      lmp_date: getYYYYMMDDFormat(new Date(startTime)),
      lmp_booking_type: isRebookSession ? 'Repeat' : 'New',
      lmp_session_balance: sessionsLeft,
      lmp_session_id: sessionDetailsStore?.uid,
      lmp_session_scheduled_duration: slot?.durationInfo.value * 60,
      lmp_session_type: vertical?.parent?.parent?.name,
      lmp_time_slot: getTime(startTime),
      topic_group_title: vertical?.parent?.name,
      topic_group_uid: vertical?.parent?.uid,
      topic_title: vertical?.name,
      topic_uid: vertical?.uid,
      ...routeGoalSegmentData,
      language:
        LANGUAGE_MAPPING[onBoardingPreferences?.selectedLanguages?.[0]?.code]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SessionDrawerHeading
        isK12={isK12}
        sessionHeader={sessionHeader}
        isWelcomeSession={isWelcomeSession}
      />
      <SessionDetailsDrawerDiv>
        <Divider />
        <SessionDetailDrawerDetails
          sessionDetails={sessionDetailsStore}
          isDarkModeOn={isDarkModeOn}
          goalUID={goalUID}
        />
        <Divider />
        <SessionDetailsForMentor
          sessionDetails={sessionDetailsStore}
          isWelcomeSession={isWelcomeSession}
        />
        {sessionDetailsStore.isRefunded ? (
          <SessionRefundStatus>
            We’ve refunded {sessionCreditsRefunded} session
            {sessionCreditsRefunded === 1 ? ' credit' : ' credits'} back to you
          </SessionRefundStatus>
        ) : null}
      </SessionDetailsDrawerDiv>
    </>
  );
};

export default SessionDetailsDrawer;
